import axios from 'axios';
import { useAuthStore } from '../store/authStore';
import { useOrgStore } from '../store/orgStore';
import { queryClient } from './queryClient';

const api = axios.create({
  baseURL: '/api/v1',
  headers: { 'Content-Type': 'application/json' },
});

// Request interceptor: Authorization + X-Organization-Id 헤더 자동 첨부
api.interceptors.request.use((config) => {
  const { accessToken } = useAuthStore.getState();
  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }
  // CHG-060: 활성 조직 ID 헤더 첨부
  const { activeOrgId } = useOrgStore.getState();
  if (activeOrgId) {
    config.headers['X-Organization-Id'] = activeOrgId;
  }
  return config;
});

// Response interceptor: 401 시 자동 토큰 갱신
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (err: unknown) => void;
}> = [];

function processQueue(error: unknown, token: string | null) {
  failedQueue.forEach(({ resolve, reject }) => {
    if (token) resolve(token);
    else reject(error);
  });
  failedQueue = [];
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // 401이 아니거나 이미 재시도한 요청이면 그대로 에러 반환
    if (error.response?.status !== 401 || originalRequest._retry) {
      return Promise.reject(error);
    }

    // 로그인/회원가입/리프레시 요청 자체의 401은 갱신 시도하지 않음
    const authPaths = ['/auth/login', '/auth/register', '/auth/refresh'];
    if (authPaths.some((p) => originalRequest.url?.includes(p))) {
      return Promise.reject(error);
    }

    // 이미 갱신 중이면 큐에 대기
    if (isRefreshing) {
      return new Promise<string>((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then((token) => {
        originalRequest._retry = true; // 큐 재시도 시 중복 갱신 방지
        originalRequest.headers.Authorization = `Bearer ${token}`;
        return api(originalRequest);
      });
    }

    originalRequest._retry = true;
    isRefreshing = true;

    const { refreshToken, setAccessToken, setRefreshToken, logout } =
      useAuthStore.getState();

    if (!refreshToken) {
      processQueue(error, null);
      logout();
      queryClient.clear();
      isRefreshing = false;
      return Promise.reject(error);
    }

    try {
      // 만료된 accessToken이라도 jwt.decode로 userId 추출 가능하므로 함께 전송
      const currentAccessToken = useAuthStore.getState().accessToken;
      const res = await axios.post(
        '/api/v1/auth/refresh',
        { refreshToken },
        currentAccessToken
          ? { headers: { Authorization: `Bearer ${currentAccessToken}` } }
          : undefined,
      );

      const newAccessToken = res.data.data.accessToken as string;
      const newRefreshToken = res.data.data.refreshToken as string;
      setAccessToken(newAccessToken);
      setRefreshToken(newRefreshToken);

      processQueue(null, newAccessToken);
      originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
      return api(originalRequest);
    } catch (refreshError) {
      processQueue(refreshError, null);
      logout();
      queryClient.clear();
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  },
);

export default api;
